import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function InvoicePrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const isPublic = location.pathname.startsWith("/public/");
  const isQuote = location.pathname.includes("/quote");
  const [inv, setInv] = useState(null);
  const [cfg, setCfg] = useState({});

  useEffect(() => {
    if (isPublic) {
      // Unauthenticated public view — fetch from /api/public/* which embeds config
      const url = isQuote
        ? `${API_BASE}/api/public/quote/${id}`
        : `${API_BASE}/api/public/sale/${id}`;
      axios.get(url).then(r => { setInv(r.data); setCfg(r.data.config || {}); })
        .catch(() => setInv({ _notfound: true }));
    } else {
      const url = isQuote ? `/quotes/${id}` : `/sales/${id}`;
      api.get(url).then(r => setInv(r.data));
      api.get("/config").then(r => setCfg(r.data)).catch(() => {});
    }
  }, [id, isQuote, isPublic]);

  const shareWhatsApp = () => {
    const cust = inv?.customer || {};
    const mobile = (cust.mobile || "").replace(/\D/g, "");
    if (!mobile) {
      toast.error("Customer ka mobile number nahi hai. Pehle customer master me add karein.");
      return;
    }
    // Prepend +91 if 10-digit local number
    const num = mobile.length === 10 ? `91${mobile}` : mobile;
    const name = cust.customer_name || cust.first_name || "Customer";
    const shareUrl = `${window.location.origin}/public/${isQuote ? "quote" : "invoice"}/${id}`;
    const docLabel = isQuote ? "Quotation" : "Invoice";
    const docNo = isQuote ? inv.quote_no : inv.invoice_no;
    const total = money(inv.total_amount || 0);
    const msg =
      `Namaste ${name},\n\n` +
      `Aapka ${docLabel} *${docNo}* — Total: *${total}*\n\n` +
      `View / Download: ${shareUrl}\n\n` +
      `Thank you,\n${cfg.company_name || "Shree Ram Enterprises"}`;
    const wa = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank");
  };

  if (inv?._notfound) return <div className="p-8 text-center text-muted-foreground">Invoice not found or link expired.</div>;
  if (!inv) return <div className="p-8">Loading...</div>;
  const c = inv.customer || {};
  const isCashMemo = !isQuote && inv.invoice_type === "CASH_MEMO";
  const docNo = isQuote ? inv.quote_no : inv.invoice_no;
  const docDate = isQuote ? inv.quote_date : inv.sale_date;
  const docLabel = isQuote ? "QUOTATION" : (isCashMemo ? "CASH MEMO" : "TAX INVOICE");
  const headerColor = isCashMemo ? "text-emerald-700" : "text-[hsl(24.6_95%_53.1%)]";
  const hasDisc = (inv.discount_amount || 0) > 0;
  const backTo = isQuote ? "/quotations" : "/sales";

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="invoice-print-page">
      <div className="max-w-4xl mx-auto">
        <div className="no-print mb-4 flex gap-2 justify-between px-6">
          {isPublic ? (
            <div className="text-xs text-muted-foreground self-center">Shared {docLabel.toLowerCase()}</div>
          ) : (
            <Button variant="outline" onClick={() => nav(backTo)} data-testid="invoice-back">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
          )}
          <div className="flex gap-2">
            {!isPublic && (
              <Button
                onClick={shareWhatsApp}
                data-testid="invoice-share-whatsapp"
                className="bg-[#25D366] hover:bg-[#20b657] text-white"
              >
                <WhatsappLogo size={16} weight="fill" className="mr-1" /> Share on WhatsApp
              </Button>
            )}
            <Button onClick={() => window.print()} data-testid="invoice-print-btn" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
              <Printer size={16} className="mr-1" /> Print / Save as PDF
            </Button>
          </div>
        </div>

        <div className="bg-white p-10 shadow-md" id="invoice-body">
          <div className="flex justify-between items-start border-b-2 border-gray-900 pb-6 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img src="/logo.png" alt="Logo" className="w-14 h-14 rounded-full bg-white" />
                <div>
                  <h1 className="font-display text-2xl font-bold">{cfg.company_name || "Shree Ram Enterprises"}</h1>
                  <p className="text-xs text-gray-700">{cfg.company_address || "D1/29 Ryan Enclave, Bhondsi, Sona Road, Gurgaon 122102"}</p>
                  <p className="text-xs text-gray-700">Mob: {cfg.company_mobile || "7617619478 / 9898241126"}</p>
                  {cfg.company_gstin && !isCashMemo && <p className="text-xs text-gray-700 font-semibold">GSTIN: {cfg.company_gstin}</p>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <h2 className={`font-display text-3xl font-bold ${headerColor}`}>{docLabel}</h2>
              <p className="text-sm mt-2"><b>{isQuote ? "Quote No" : (isCashMemo ? "Memo No" : "Invoice No")}:</b> {docNo}</p>
              <p className="text-sm"><b>Date:</b> {docDate}</p>
              {isQuote && inv.valid_until && <p className="text-sm"><b>Valid Until:</b> {inv.valid_until}</p>}
              {isQuote && inv.status && <p className="text-xs mt-1 uppercase tracking-wider font-semibold text-gray-600">Status: {inv.status}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Bill To</p>
              <p className="font-semibold">{c.customer_name}</p>
              <p className="text-sm text-gray-700">{c.address}</p>
              <p className="text-sm text-gray-700">{c.city} {c.state}</p>
              <p className="text-sm text-gray-700">Mob: {c.mobile}</p>
              {c.gst_no && <p className="text-sm text-gray-700">GSTIN: {c.gst_no}</p>}
            </div>
            {(inv.ship_to_name || inv.ship_to_address || c.shipping_street1) && (
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Ship To</p>
                <p className="font-semibold">{inv.ship_to_name || c.shipping_attention || c.customer_name}</p>
                <p className="text-sm text-gray-700">{inv.ship_to_address || [c.shipping_street1, c.shipping_street2].filter(Boolean).join(", ")}</p>
                <p className="text-sm text-gray-700">{inv.ship_to_city || c.shipping_city || ""} {inv.ship_to_state || c.shipping_state || ""} {c.shipping_pincode || ""}</p>
                {(inv.ship_to_mobile || c.mobile) && <p className="text-sm text-gray-700">Mob: {inv.ship_to_mobile || c.mobile}</p>}
                {(inv.ship_to_gst || c.gst_no) && <p className="text-sm text-gray-700">GSTIN: {inv.ship_to_gst || c.gst_no}</p>}
              </div>
            )}
          </div>

          <table className="w-full border-collapse mb-6">
            <thead>
              <tr className="bg-gray-900 text-white text-sm">
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Item</th>
                {!isCashMemo && <th className="text-left p-2">HSN</th>}
                <th className="text-right p-2">Qty</th>
                <th className="text-right p-2">Rate</th>
                {hasDisc && <th className="text-right p-2">Disc</th>}
                {hasDisc && <th className="text-right p-2">Taxable</th>}
                {!isCashMemo && <th className="text-right p-2">GST%</th>}
                {!isCashMemo && (inv.tax_type === "CGST_SGST" ? (<>
                  <th className="text-right p-2">CGST</th>
                  <th className="text-right p-2">SGST</th>
                </>) : (
                  <th className="text-right p-2">IGST</th>
                ))}
                <th className="text-right p-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((li, i) => (
                <tr key={i} className="border-b border-gray-200 text-sm">
                  <td className="p-2">{i + 1}</td>
                  <td className="p-2">{li.item_name} <span className="text-xs text-gray-500">({li.item_code})</span></td>
                  {!isCashMemo && <td className="p-2 text-xs">{li.hsn_code || "-"}</td>}
                  <td className="p-2 text-right tabular-nums">{li.qty}</td>
                  <td className="p-2 text-right tabular-nums">{money(li.price)}</td>
                  {hasDisc && <td className="p-2 text-right tabular-nums text-xs text-orange-700">-{money(li.discount || 0)}</td>}
                  {hasDisc && <td className="p-2 text-right tabular-nums text-xs">{money(li.taxable_amount ?? (li.amount - (li.discount || 0)))}</td>}
                  {!isCashMemo && <td className="p-2 text-right">{li.gst_rate}%</td>}
                  {!isCashMemo && (inv.tax_type === "CGST_SGST" ? (<>
                    <td className="p-2 text-right tabular-nums text-xs">{money(li.cgst || li.tax/2)}</td>
                    <td className="p-2 text-right tabular-nums text-xs">{money(li.sgst || li.tax/2)}</td>
                  </>) : (
                    <td className="p-2 text-right tabular-nums text-xs">{money(li.igst || li.tax)}</td>
                  ))}
                  <td className="p-2 text-right tabular-nums font-semibold">{money(li.total || (li.qty * li.price))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span>Sub Total:</span><span className="tabular-nums">{money(inv.sub_total)}</span></div>
              {inv.discount_amount > 0 && (
                <>
                  <div className="flex justify-between text-orange-700">
                    <span>Discount{inv.discount_type === "PERCENT" ? ` (${inv.discount_value}%)` : ""}:</span>
                    <span className="tabular-nums">- {money(inv.discount_amount)}</span>
                  </div>
                  <div className="flex justify-between"><span>Taxable Value:</span><span className="tabular-nums">{money(inv.taxable_total || (inv.sub_total - inv.discount_amount))}</span></div>
                </>
              )}
              {!isCashMemo && (inv.tax_type === "CGST_SGST" ? (<>
                <div className="flex justify-between"><span>CGST:</span><span className="tabular-nums">{money(inv.cgst_total ?? inv.tax_total/2)}</span></div>
                <div className="flex justify-between"><span>SGST:</span><span className="tabular-nums">{money(inv.sgst_total ?? inv.tax_total/2)}</span></div>
              </>) : (
                <div className="flex justify-between"><span>IGST:</span><span className="tabular-nums">{money(inv.igst_total ?? inv.tax_total)}</span></div>
              ))}
              {Math.abs(inv.round_off || 0) > 0.001 && (
                <div className="flex justify-between">
                  <span>Round Off:</span>
                  <span className="tabular-nums">{inv.round_off > 0 ? "+" : "-"}{money(Math.abs(inv.round_off))}</span>
                </div>
              )}
              <div className="flex justify-between border-t-2 border-gray-900 pt-2 mt-2 font-display text-lg font-bold">
                <span>Grand Total:</span><span className="tabular-nums">{money(inv.total_amount)}</span>
              </div>
            </div>
          </div>

          {inv.remarks && <div className="mt-6 text-sm"><b>Remarks:</b> {inv.remarks}</div>}

          {(cfg.bank_name || cfg.bank_account_no || cfg.upi_id) && (
            <div className="mt-6 border border-gray-300 rounded p-3 bg-gray-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-600 mb-1">Payment Details — Bank / UPI</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-800">
                {cfg.bank_account_holder && <div><b>Account Holder:</b> {cfg.bank_account_holder}</div>}
                {cfg.bank_name && <div><b>Bank:</b> {cfg.bank_name}</div>}
                {cfg.bank_account_no && <div><b>A/c No:</b> {cfg.bank_account_no}</div>}
                {cfg.bank_ifsc && <div><b>IFSC:</b> {cfg.bank_ifsc}</div>}
                {cfg.bank_branch && <div><b>Branch:</b> {cfg.bank_branch}</div>}
                {cfg.upi_id && <div><b>UPI ID:</b> {cfg.upi_id}</div>}
              </div>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-4">
            <div className="text-xs text-gray-600">
              <p><b>Terms:</b> {cfg.invoice_footer_note || "Goods once sold will not be taken back. E&OE."}</p>
              <p className="mt-2">Thank you for your business!</p>
            </div>
            <div className="text-right">
              <div className="text-xs mb-2 font-semibold">For {cfg.company_name || "Shree Ram Enterprises"}</div>
              <div className="border-t border-gray-400 mt-12 pt-2 text-xs">Authorized Signatory</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
